import { ToastManager } from '@inter-ikea-kompis/component-toast';
import { ConfigurationSummaryToastMessage } from '@inter-ikea-kompis/component-configuration-summary';
import { AddToBagToastMessage } from '@inter-ikea-kompis/component-add-to-bag';
import { AddToListToastMessage } from '@inter-ikea-kompis/component-add-to-list';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { translate } from '../services/L10n';
import { t } from '../translations';
import { selectKompisTranslations } from '../state/translations/translationsSelectors';
import { selectKompisDexfSettings } from '../state/dexfSettings/dexfSettingsSelectors';
import store from '../state';

const toastManager = new ToastManager({ theme: SkapaTheme });

type Toast = {
  label: string;
  actionHref?: string;
  actionLabel?: string;
};

const toastQueue: Array<Toast> = [];
let queueIsRunning: boolean = false;
let runQueuePromise: Promise<void> | null = null;

/**
 * Queues the TAC toast and returns a promise, to handle
 * several toasts being shown sequentially.
 * @param {string} translationKey The translation key for the message in the toast.
 */
export function showTacToast(translationKey: string): void {
  const toast: Toast = { label: translate(t[translationKey]) };
  queueToast(toast);
}

/**
 * Queues the amelioration toast and returns a promise, to handle
 * several toasts being shown sequentially.
 * @param {string} message The message to be shown in the toast.
 */
export function showAmeliorationToast(message: string): void {
  const toast: Toast = { label: message };
  queueToast(toast);
}

/**
 * Queues the toast and returns a promise, to handle
 * several toasts being shown sequentially.
 * @param {number} bulkSize The number of pieces in the multipack.
 * @returns {Promise<void>} A promise that is resolved when the toast is dismissed.
 */
export function showMultipackToast(bulkSize: number): Promise<void> {
  const parameterName = 'nr_of_articles';
  const message = translate(t.TOAST_MULTIPACK_INFO).replaceAll(
    '{{' + parameterName + '}}',
    bulkSize.toString()
  );
  const toast: Toast = { label: message };
  return queueToast(toast);
}

/**
 * Displays a toast message depending on if;
 * 1) An product was added to the bag
 * 2) An product was added to the list
 * and whether the addition of the product was succesful.
 * @param {string} destination Either cart or list.
 * @param {boolean} success  If the product was successfully added to the bag/list.
 * @param {boolean} ymal If we're coming from ymal.
 * @returns {void}
 */
export async function renderToast(
  destination: string,
  success: boolean,
  ymal: boolean
) {
  const kompisTranslations = selectKompisTranslations(store.getState());
  const kompisUrls = selectKompisDexfSettings(store.getState()).urls;

  const addToCartError =
    ConfigurationSummaryToastMessage.getAddToCartFail(kompisTranslations);
  const addToListError =
    ConfigurationSummaryToastMessage.getAddToListFail(kompisTranslations);
  const addToCartYmalSuccessMessage = AddToBagToastMessage.getAddToCartSuccess(
    kompisTranslations,
    kompisUrls
  );
  const addToCartYmalErrorMessage = AddToBagToastMessage.getAddToCartFailure(
    kompisTranslations,
    kompisUrls
  );
  const addToListYmalSuccessMessage = AddToListToastMessage.getAddToListSuccess(
    kompisTranslations,
    kompisUrls
  );
  const addToListYmalErrorMessage = AddToListToastMessage.getAddToListFailure(
    kompisTranslations,
    kompisUrls
  );

  let toast: Toast = { label: '', actionHref: '', actionLabel: '' };
  if (destination === 'cart') {
    ymal
      ? (toast = success
          ? addToCartYmalSuccessMessage
          : addToCartYmalErrorMessage)
      : !success && (toast = addToCartError);
    return showToast(toast);
  }
  ymal
    ? (toast = success
        ? addToListYmalSuccessMessage
        : addToListYmalErrorMessage)
    : !success && (toast = addToListError);
  return showToast(toast);
}

/**
 * Queues the toast.
 * If the queue is already running it only pushes the the toast into the queue.
 * If the queue is not running it pushes the toast to the queue and runs the queue.
 * @param {Toast} toast
 * @returns {Promise<void>}
 */
async function queueToast(toast: Toast): Promise<void> {
  toastQueue.push(toast);
  if (queueIsRunning) {
    return;
  } else {
    runQueuePromise = runQueue();
  }
}

/**
 * Asynchronously runs a queue until the queue is empty.
 * Awaits the resolving of one toast until it shows the next toast.
 */
async function runQueue() {
  queueIsRunning = true;
  while (!!toastQueue.length) {
    const toastToRun = toastQueue.shift();
    await showToast(toastToRun);
  }
  queueIsRunning = false;
}

/**
 * Returns a promise that resolves when all queued toasts have
 * been shown (and the last one has timed out and closed).
 * @returns {Promise<void>}
 */
async function noMorePendingToasts(): Promise<void> {
  if (queueIsRunning && runQueuePromise) {
    await runQueuePromise;
  }
}

/**
 * Calculates the lifespan-time of the toast to be shown, shows the toast, and then sets a timeout
 * that resolves once that lifespan-time has passed.
 * @param {Toast} toast
 * @returns {Promise<void>}
 */
async function showToast(toast: Toast | undefined): Promise<void> {
  if (!toast) return Promise.resolve();
  const toastLifespan = calculateToastLifespan(toast.label);
  return new Promise((resolve, reject) => {
    toastManager.showToast(toast);
    setTimeout(async () => {
      resolve();
    }, toastLifespan);
  });
}

/**
 * Resets all toasts. For example when going from scene to start view.
 */
export function stopAndResetQueuedToasts() {
  dismissCurrentToast();
  queueIsRunning = false;
  while (toastQueue.length) {
    toastQueue.pop();
  }
}

/**
 * Calculate the lifespan of the toast based on the amount of characters in the toast label,
 * and a default max and min value.
 * Values retrieved from {@link https://skapa.ikea.net/components/messages/toast}
 * @param toastLabel
 * @returns {number} toastLifespan in milliseconds.
 */
function calculateToastLifespan(toastLabel: string): number {
  const defaultMax: number = 10000;
  const defaultMin: number = 5000;
  const readTimeOneChar: number = 50;

  let toastLifespan: number = readTimeOneChar * toastLabel.length;

  if (toastLifespan < defaultMin) toastLifespan = defaultMin;
  if (toastLifespan > defaultMax) toastLifespan = defaultMax;

  return toastLifespan;
}

/**
 * Hides the toast that is currently shown.
 */
export function dismissCurrentToast(): void {
  toastManager.hideToast();
}

export default {
  showTacToast,
  dismissCurrentToast,
  noMorePendingToasts,
};
